import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isAxiosError } from "axios";
import LoadingSpinner from "components/LoadingSpinner";
import { useAppDispatch } from "app/hooks";
import { fetchUser } from "app/userSlice";
import useReduxUtils from "hooks/useReduxUtils";
import AuthService, { UserInfo } from "services/authService";
import TokenService from "services/tokenService";
import UserService from "services/userService";
import { StorageUtils } from "utils/storageUtils";
import { closeHanpassWebView } from "utils/hanpassUtils";

const toInternalPath = (pathOrUrl: string, fallback = "/") => {
  const normalized = (pathOrUrl || "").trim();
  if (!normalized) return fallback;

  if (/^https?:\/\//i.test(normalized)) {
    try {
      const parsed = new URL(normalized);
      return `${parsed.pathname}${parsed.search}${parsed.hash}` || fallback;
    } catch (_error) {
      return fallback;
    }
  }

  return normalized.startsWith("/") ? normalized : `/${normalized}`;
};

const buildSignupPath = (
  redirectUrl: string,
  userInfo: UserInfo,
  fallbackLanguage: string
) => {
  const params = new URLSearchParams();
  params.set("provider", "Hanpass");
  params.set("memberSeq", userInfo.memberSeq || "");
  params.set("firstName", userInfo.firstName || "");
  params.set("middleName", userInfo.middleName || "");
  params.set("lastName", userInfo.lastName || "");
  params.set("email", userInfo.email || "");
  params.set("phoneNumber", userInfo.phoneNumber || "");
  params.set("nationality", userInfo.nationality || "");
  params.set("language", userInfo.language || fallbackLanguage);

  const path = toInternalPath(redirectUrl, "/hp-sign-up");
  const queryString = params.toString();
  return queryString
    ? path.includes("?")
      ? `${path}&${queryString}`
      : `${path}?${queryString}`
    : path;
};

const LoginProviderV2Page = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetStore } = useReduxUtils({ retainToken: false });
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const run = async () => {
      TokenService.removeAccessToken();
      resetStore();

      const memberSeq =
        searchParams.get("memberSeq") || searchParams.get("member_seq") || "";
      const session = searchParams.get("session") || "";
      const language = searchParams.get("language") || "en";
      const tab = searchParams.get("tab") || "";
      const tabPath =
        !tab || tab.toLowerCase() === "ktx"
          ? "/"
          : tab.startsWith("/")
          ? tab
          : `/${tab}`;
      const link =
        searchParams.get("link") ||
        searchParams.get("initialLink") ||
        tabPath;

      if (!memberSeq || !session) {
        closeHanpassWebView();
        return;
      }

      try {
        const response = await AuthService.hanpassLogin({
          memberSeq,
          session,
          language,
          link,
        });

        const accessToken = response.data?.accessToken;
        if (!accessToken) {
          if (response.data?.redirectUrl && response.data?.userInfo) {
            const signupPath = buildSignupPath(
              response.data.redirectUrl,
              response.data.userInfo,
              language
            );
            navigate(signupPath, { replace: true });
            return;
          }
          throw new Error("Unexpected /hanpass/login response");
        }

        TokenService.setAccessToken(accessToken);

        const userInfoResponse = await UserService.getMyInfo();
        dispatch(fetchUser(userInfoResponse));
        StorageUtils.SavedLoginId.set(userInfoResponse.loginId);

        navigate(toInternalPath(response.data?.redirectUrl || "/", "/"), {
          replace: true,
        });
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 400) {
          const payload = error.response.data as {
            redirectUrl?: string;
            userInfo?: UserInfo;
            errorMessage?: string;
          };

          
          if (payload?.redirectUrl && payload?.userInfo) {
            const signupPath = buildSignupPath(
              payload.redirectUrl,
              payload.userInfo,
              language
            );
            navigate(signupPath, { replace: true });
            return;
          }

          if (payload?.errorMessage) {
            alert(payload.errorMessage);
          }
        }
        closeHanpassWebView();
      }
    };

    run();
  }, [dispatch, navigate, resetStore, searchParams]);

  return <LoadingSpinner />;
};

export default LoginProviderV2Page;
